import { Autocomplete, Chip, Stack, TextField } from '@mui/material'
import React from 'react'
import AlertDialog from 'src/components/Dialog'
import { useAdminPermission } from 'src/hooks/IAM/AdminPermission/useAdminPermission'
import { useUserIAM } from 'src/hooks/IAM/AdminUser/useUserIAM'
import { IUser } from 'src/services/IAM/AdminUser/AdminUser.interface'
import StepperModal from 'src/views/AdminUser/components/StepperModal'
import { useAdminGroup } from 'src/hooks/IAM/AdminGroups/useAdminGroups'

/**
 * El componente `CreateGroup` permite la creación de un nuevo grupo. Proporciona campos para ingresar 
 * el nombre y la descripción del grupo, seleccionar usuarios y asignar permisos.
 *
 * @returns {JSX.Element} El componente `CreateGroup`.
 * @component
 * @example
 * ```tsx
 * import React from 'react';
 * import CreateGroup from './CreateGroup';
 *
 * const App = () => {
 *   return (
 *     <div>
 *       <CreateGroup />
 *     </div>
 *   );
 * }
 * export default App;
 * ```
 */
const CreateGroup = (): JSX.Element => {
  // Hooks personalizados para la gestión de grupos, usuarios y permisos
  const { group, setGroupAdmin } = useAdminGroup()
  const { usersIAM } = useUserIAM()
  const {
    selectedPermissions,
    permissions,
    loadPermissions,
    setSelectPermission,
  } = useAdminPermission()

  return (
    <Stack direction={'row'} gap={2} mb={2} justifyContent={'flex-start'}>
      {/* Campo de texto para el nombre del grupo */}
      <TextField
        sx={{
          flex: 1,
        }}
        id="outlined-basic"
        label="Nombre nuevo grupo"
        variant="outlined"
        onChange={(e) => setGroupAdmin({ ...group, groupName: e.target.value })}
        required
      />

      {/* Campo de texto para la descripción del grupo */}
      <TextField
        sx={{
          flex: 1,
        }}
        id="outlined-basic"
        label="Descripción"
        variant="outlined"
        onChange={(e) =>
          setGroupAdmin({ ...group, description: e.target.value })
        }
        required
      />

      {/* Autocompletado para seleccionar los usuarios del grupo */}
      <Autocomplete
        sx={{ flex: 1 }}
        disablePortal
        id="combo-box-demo"
        options={usersIAM}
        multiple
        value={group.users}
        onChange={(event, newValue: IUser[]) => {
          setGroupAdmin({
            ...group,
            users: newValue,
          })
        }}
        getOptionLabel={(option) => `${option.userDocType} - ${option.userDocId}`}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option: IUser, index) => (
            <Chip
              label={`${option.userDocType} - ${option.userDocId}`}
              {...getTagProps({ index })}
              key={index}
            />
          ))
        }
        renderInput={(params) => <TextField {...params} label="Usuarios" />}
      />

      {/* Diálogo para agregar permisos al grupo */}
      <AlertDialog
        title="Permisos"
        buttonLabel={`Agregar permisos: ${selectedPermissions.length}`}
        maxWidth="lg"
        children={
          <Stack direction={'row'}>
            <StepperModal
              handlerSelectedPermissions={setSelectPermission}
              selectedPermissions={selectedPermissions}
              loadPermissions={loadPermissions}
              permissionsProp={permissions}
            />
          </Stack>
        }
      />
    </Stack>
  )
}

export default CreateGroup
